/* @jsx h */
import { defineComponent, h } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import { cn, useLocalization } from "@lemmonhq/uikit";

export type GiftCardPreviewProps = {
  productName: string;
  restaurantName: string;
  amount: number | null;
  currency: string;
  isTableCredit: boolean;
  /** Chosen table, table credit only. */
  tableName?: string | null;
  /** Credit the chosen table already has, table credit only. */
  currentBalance?: number | null;
};

/**
 * The card being sold, drawn as a card: it fills in as the cashier picks the
 * amount and table, so the choice is easy to read back to the guest.
 */
export const GiftCardPreview = defineComponent<GiftCardPreviewProps>({
  name: "GiftCardPreview",
  props: [
    "productName",
    "restaurantName",
    "amount",
    "currency",
    "isTableCredit",
    "tableName",
    "currentBalance",
  ],
  setup(props) {
    const { t } = useLocalization();
    const money = (value: number, decimals = 2) =>
      formatPrice(value, true, decimals, props.currency);

    return () => {
      const hasBalance =
        props.isTableCredit &&
        !!props.tableName &&
        (props.currentBalance ?? 0) > 0;
      return (
        <div
          class={cn(
            "relative isolate flex items-center gap-3 overflow-hidden rounded-2xl px-3 py-2.5 shadow-md sm:px-4",
            "text-primary-content",
            // Table credit is plain brand blue; gift cards keep the gradient
            // from the lighter info blue into the dark secondary.
            props.isTableCredit
              ? "bg-primary"
              : "bg-gradient-to-br from-info via-primary to-secondary",
          )}
        >
          {props.isTableCredit ? null : (
            <>
              {/* Decorative ring, kept behind the content. */}
              <span class="pointer-events-none absolute -right-6 -top-10 -z-10 h-24 w-24 rounded-full border-[16px] border-primary-content/10" />
              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-content/15">
                <span class="material-symbols-outlined text-xl">redeem</span>
              </span>
            </>
          )}

          <span class="min-w-0 flex-1 leading-tight">
            {props.isTableCredit ? (
              <span
                class={cn(
                  "block truncate text-sm font-semibold",
                  !props.tableName && "opacity-70",
                )}
              >
                {props.tableName || t("pos.gift_card.no_table_yet")}
              </span>
            ) : (
              <span class="block truncate text-sm font-semibold">
                {props.productName}
              </span>
            )}
            <span class="block truncate text-xs tabular-nums opacity-75">
              {hasBalance ? (
                <>
                  {money(props.currentBalance!, 0)}
                  {" → "}
                  <span class="font-semibold">
                    {money(
                      (props.currentBalance || 0) + (props.amount || 0),
                      0,
                    )}
                  </span>
                </>
              ) : (
                props.restaurantName
              )}
            </span>
          </span>

          <span
            class={cn(
              "shrink-0 text-2xl font-bold tabular-nums tracking-tight",
              !props.amount && "opacity-40",
            )}
          >
            {money(props.amount || 0)}
          </span>
        </div>
      );
    };
  },
});
